import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, CheckCircle, User, Flask, CalendarBlank, Warning, FileText } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MergeVolunteersDialog({ open, onClose, volunteerA, volunteerB, lang, onMerged }) {
  const [linkedA, setLinkedA] = useState(null);
  const [linkedB, setLinkedB] = useState(null);
  const [keepId, setKeepId] = useState(null);
  const [merging, setMerging] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !volunteerA || !volunteerB) return;
    setKeepId(null);
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/volunteers/${volunteerA.volunteer_id}/linked-data`, { withCredentials: true }),
      axios.get(`${API_URL}/api/volunteers/${volunteerB.volunteer_id}/linked-data`, { withCredentials: true }),
    ]).then(([a, b]) => {
      setLinkedA(a.data);
      setLinkedB(b.data);
    }).catch(() => toast.error('Error loading data'))
      .finally(() => setLoading(false));
  }, [open, volunteerA, volunteerB]);

  const handleMerge = async () => {
    if (!keepId) return;
    const removeId = keepId === volunteerA.volunteer_id ? volunteerB.volunteer_id : volunteerA.volunteer_id;
    setMerging(true);
    try {
      const r = await axios.post(`${API_URL}/api/volunteers/merge`, { keep_id: keepId, remove_id: removeId }, { withCredentials: true });
      const re = r.data.reassigned || {};
      toast.success(lang === 'es'
        ? `Fusionados. Reasignados: ${re.study_volunteers || 0} estudios, ${re.appointments || 0} citas, ${re.adverse_effects || 0} EA`
        : `Merged. Reassigned: ${re.study_volunteers || 0} studies, ${re.appointments || 0} appointments, ${re.adverse_effects || 0} AE`);
      onMerged();
    } catch (err) {
      toast.error(errText(err));
    }
    setMerging(false);
  };

  if (!volunteerA || !volunteerB) return null;

  const lb = lang === 'es' ? {
    title: 'Fusionar Voluntarios Duplicados',
    desc: 'Selecciona cual conservar. Los datos del eliminado se reasignaran al conservado.',
    keep: 'Conservar', remove: 'Eliminar', merge: 'Fusionar', cancel: 'Cancelar',
    studies: 'Estudios', appointments: 'Citas', adverseEffects: 'Efectos adversos',
    responses: 'Respuestas', consents: 'Consentimientos', loading: 'Cargando...',
    selectOne: 'Selecciona cual conservar', merging: 'Fusionando...',
    warning: 'Esta accion no se puede deshacer. El voluntario eliminado desaparecera permanentemente.',
  } : {
    title: 'Merge Duplicate Volunteers',
    desc: 'Select which to keep. Data from the removed one will be reassigned to the kept one.',
    keep: 'Keep', remove: 'Remove', merge: 'Merge', cancel: 'Cancel',
    studies: 'Studies', appointments: 'Appointments', adverseEffects: 'Adverse effects',
    responses: 'Responses', consents: 'Consents', loading: 'Loading...',
    selectOne: 'Select which to keep', merging: 'Merging...',
    warning: 'This action cannot be undone. The removed volunteer will be permanently deleted.',
  };

  const renderCard = (vol, linked, isKeep) => {
    const selected = keepId === vol.volunteer_id;
    const isRemove = keepId && !selected;
    return (
      <button
        type="button"
        onClick={() => setKeepId(vol.volunteer_id)}
        data-testid={`merge-select-${vol.volunteer_id}`}
        className={`flex-1 p-4 rounded border-2 text-left transition-all ${
          selected ? 'border-emerald-400 bg-emerald-50 shadow-sm' :
          isRemove ? 'border-red-300 bg-red-50/50 opacity-70' :
          'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
        }`}
      >
        {selected && (
          <Badge className="mb-2 bg-emerald-600 text-white text-[10px]">
            <CheckCircle size={10} className="mr-1" /> {lb.keep}
          </Badge>
        )}
        {isRemove && (
          <Badge variant="outline" className="mb-2 text-[10px] bg-red-50 text-red-600 border-red-300">
            {lb.remove}
          </Badge>
        )}
        <div className="flex items-center gap-2 mb-2">
          <User size={16} className="text-stone-400" />
          <span className="text-sm font-semibold text-stone-900">{vol.display_name}</span>
        </div>
        <div className="space-y-1 text-xs text-stone-600">
          <p className="font-mono">{vol.volunteer_id}</p>
          <p>DNI: {vol.personal_data?.dni || '-'}</p>
          <p>Email: {vol.personal_data?.email || '-'}</p>
          <p>Tel: {vol.personal_data?.telefono || '-'}</p>
        </div>
        {linked && (
          <div className="mt-3 pt-3 border-t border-stone-200 grid grid-cols-2 gap-1.5 text-[11px]">
            <div className="flex items-center gap-1.5">
              <Flask size={12} className="text-[#1f3e80]" />
              <span>{linked.studies} {lb.studies}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CalendarBlank size={12} className="text-stone-400" />
              <span>{linked.appointments} {lb.appointments}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Warning size={12} className="text-amber-500" />
              <span>{linked.adverse_effects} {lb.adverseEffects}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <FileText size={12} className="text-stone-400" />
              <span>{linked.consents} {lb.consents}</span>
            </div>
          </div>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => { if (!merging) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="merge-volunteers-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.title}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">{lb.desc}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-xs text-stone-400 text-center py-8">{lb.loading}</p>
        ) : (
          <>
            <div className="flex gap-3 items-stretch">
              {renderCard(volunteerA, linkedA, keepId === volunteerA.volunteer_id)}
              <div className="flex items-center">
                <ArrowRight size={20} className="text-stone-300" />
              </div>
              {renderCard(volunteerB, linkedB, keepId === volunteerB.volunteer_id)}
            </div>
            {keepId && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800 flex items-start gap-2">
                <Warning size={14} className="flex-shrink-0 mt-0.5" />
                {lb.warning}
              </div>
            )}
          </>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={merging} className="text-xs">{lb.cancel}</Button>
          <Button
            onClick={handleMerge}
            disabled={!keepId || merging}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5"
            data-testid="confirm-merge-btn"
          >
            {merging ? lb.merging : lb.merge}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
